import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@startupquickstart/frontend';

import { LocationPicker } from '@/components/shared';

interface PinLocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  latitude: number | null;
  longitude: number | null;
  googleMapsApiKey?: string;
  saving?: boolean;
  onSave: (latitude: number, longitude: number) => void;
}

function PinLocationDialogBody({
  latitude,
  longitude,
  googleMapsApiKey,
  saving,
  onSave,
  onClose,
}: {
  latitude: number | null;
  longitude: number | null;
  googleMapsApiKey?: string;
  saving?: boolean;
  onSave: (latitude: number, longitude: number) => void;
  onClose: () => void;
}) {
  // Seeded once from props — the body remounts each time the dialog opens.
  const [lat, setLat] = useState(latitude != null ? String(latitude) : '');
  const [lng, setLng] = useState(longitude != null ? String(longitude) : '');

  const parsedLat = parseFloat(lat);
  const parsedLng = parseFloat(lng);
  const hasValidPin = Number.isFinite(parsedLat) && Number.isFinite(parsedLng);

  return (
    <>
      <LocationPicker
        latitude={lat}
        longitude={lng}
        onLocationChange={(nextLat, nextLng) => {
          setLat(nextLat);
          setLng(nextLng);
        }}
        googleMapsApiKey={googleMapsApiKey}
      />
      <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={() => onSave(parsedLat, parsedLng)}
          loading={saving}
          disabled={!hasValidPin || saving}
        >
          Save location
        </Button>
      </DialogFooter>
    </>
  );
}

/**
 * Modal for editing a single challenge's pin via the draggable/clickable
 * `LocationPicker`, used from the challenge → template → event detail surfaces.
 * The caller supplies `onSave` to persist to the right endpoint for its context.
 */
export function PinLocationDialog({
  open,
  onOpenChange,
  title = 'Edit location',
  description = 'Click the map or drag the pin to set the location.',
  ...rest
}: PinLocationDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {open ? <PinLocationDialogBody {...rest} onClose={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}
